import { MimeType } from 'vet-bones/constants';

import { DataSourceTypeDescription } from 'src/constants/dataSourceTypeDescription';
import {
  Mapping,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import { DiagramSnapshot } from 'src/containers/ProjectDiagram/types/diagram';
import { dataSourceNativeQuoteCharacter } from 'src/selectors/dataSourceNativeQuoteCharacter';
import {
  ProjectVirtualGraph,
  ProjectVirtualGraphOptions,
  defaultProjectVirtualGraphOptions,
} from 'src/store/state/projects';
import { newline } from 'src/templates/copy';
import { ExportVirtualGraphOptions } from 'src/utils/export/types';
import { generateSqlQueryForTable } from 'src/utils/generateSqlQueryForTable';
import { stringifyVirtualGraphMappings } from 'src/utils/stringify/mappings/virtualGraphMappings';
import { stringifySparqlNamespaces } from 'src/utils/stringify/namespaces';
import { stringifyProperties } from 'src/utils/stringify/properties';
import { stringToFilename } from 'src/utils/stringToFilename';

const exportMappings = ({
  id,
  diagram,
  dataNamespace,
  namespaces,
  typeDescription,
  virtualGraphsAndMappings,
}: {
  id: string;
  diagram: DiagramSnapshot;
  dataNamespace: string;
  namespaces: Record<string, string>;
  typeDescription: DataSourceTypeDescription;
  virtualGraphsAndMappings: Array<{
    classMappings: Mapping[];
    relationshipMappings: RelationshipMapping[];
    virtualGraph: ProjectVirtualGraph;
  }>;
}) => {
  const filename = `${stringToFilename(id)}.sms`;
  const namespacesString = stringifySparqlNamespaces(namespaces);
  const stringifiedMappings = virtualGraphsAndMappings
    .map(({ classMappings, relationshipMappings, virtualGraph }) => {
      const nativeQuoteCharacter = dataSourceNativeQuoteCharacter(
        typeDescription,
        virtualGraph.dataSourceType
      );
      const query =
        typeof virtualGraph.from === 'string'
          ? virtualGraph.from
          : generateSqlQueryForTable({
              table: virtualGraph.from,
              nativeQuoteCharacter,
              options: virtualGraph.options,
            });
      const mappingString = stringifyVirtualGraphMappings({
        classMappings,
        model: diagram.model,
        relationshipMappings,
        dataNamespace,
        namespaces,
        query,
      });
      return `# ${virtualGraph.name}\n${mappingString}`;
    })
    .join('\n\n;\n\n');
  const contents =
    [namespacesString, stringifiedMappings].join('\n\n') + newline;
  return { contents, filename, mimeType: MimeType.SMS };
};

const exportProperties = (id: string, options: ProjectVirtualGraphOptions) => {
  const filename = `${stringToFilename(id)}.properties`;
  const contents = stringifyProperties(options) + newline;
  return { contents, filename, mimeType: MimeType.TEXT };
};

export const exportVirtualGraph = ({
  id,
  diagram,
  dataNamespace,
  namespaces,
  typeDescription,
  virtualGraphsAndMappings,
}: {
  id: string;
  diagram: DiagramSnapshot;
  dataNamespace: string;
  namespaces: Record<string, string>;
  typeDescription: DataSourceTypeDescription;
  virtualGraphsAndMappings: Array<{
    classMappings: Mapping[];
    relationshipMappings: RelationshipMapping[];
    virtualGraph: ProjectVirtualGraph;
  }>;
}): ExportVirtualGraphOptions => {
  const mappingsOptions = exportMappings({
    id,
    diagram,
    dataNamespace,
    namespaces,
    typeDescription,
    virtualGraphsAndMappings,
  });

  const propertiesOptions = exportProperties(
    id,
    virtualGraphsAndMappings[0].virtualGraph.options ||
      defaultProjectVirtualGraphOptions
  );

  return { mappingsOptions, propertiesOptions };
};
